/** The library's filters in the address bar, both ways. Pure, so it can be tested. */
import type { LibraryFilters } from './api';

/**
 * Where exercises live in the app: the one Exercises page (it replaced "Find exercises",
 * `/library`, and "My exercises", `/write`, which redirect here), an exercise open on it,
 * and the editor of one written in Studio.
 */
export const EXERCISES_PAGE = '/exercises';
/** each segment encoded, as `pathId` in api.ts: an archive or repository id ends in `#<n>` */
const pathId = (id: string): string => id.split('/').map(encodeURIComponent).join('/');
export const exercisePath = (id: string): string => `${EXERCISES_PAGE}/${pathId(id)}`;
/** only exercises written in Studio (`user/…`) have an editor; the others get an edited version first */
export const exerciseEditPath = (id: string): string => `${EXERCISES_PAGE}/${pathId(id)}/edit`;

/** The page's views beside its sources: work assigned to me, exercises I created. */
export type ExercisesView = 'assigned' | 'created';
export const viewOf = (params: URLSearchParams): ExercisesView | undefined => {
  const v = params.get('view');
  return v === 'assigned' || v === 'created' ? v : undefined;
};

export const LIST_KEYS = [
  'years', 'sheets', 'topics', 'types', 'goals', 'motifs', 'difficultyRated', 'difficultySource',
  'defectKind', 'defectSeverity', 'codeLanguages', 'proseLanguages', 'sheetKinds',
] as const;
export const NUMBER_KEYS = [
  'difficultyMin', 'difficultyMax', 'difficultySourceMin', 'difficultySourceMax', 'timeMin', 'timeMax',
] as const;
export const BOOL_KEYS = ['categorisedOnly', 'hasSolution', 'noSeriousProblems', 'hasTypst'] as const;

/**
 * The library's other sources: exercises written in the app, the course repository's, and
 * the 2026 Typst archive's that no 2025 exercise matches (Phase 8).
 */
const sourceOf = (value: string | null): LibraryFilters['source'] =>
  value === 'authored' || value === 'gitlab' || value === 'archive' ? value : undefined;

/** Filters out of a URL. Every key the page can set, so a shared link shows the same list. */
export function filtersFromParams(params: URLSearchParams): LibraryFilters {
  const f: LibraryFilters = {
    /*
     * Absent means automatic: best match while searching, newest first otherwise. A fixed
     * 'year-desc' default could not be told from a chosen "Newest first", so choosing it
     * during a search snapped back to best match.
     */
    sort: params.get('sort') || undefined,
    source: sourceOf(params.get('source')),
  };
  for (const key of LIST_KEYS) {
    const values = params.getAll(key).flatMap((v) => v.split(',')).filter(Boolean);
    if (values.length) (f as Record<string, unknown>)[key] = values;
  }
  for (const key of NUMBER_KEYS) {
    const raw = params.get(key);
    if (raw !== null && raw !== '' && Number.isFinite(Number(raw))) (f as Record<string, unknown>)[key] = Number(raw);
  }
  for (const key of BOOL_KEYS) if (params.get(key) === '1') (f as Record<string, unknown>)[key] = true;
  if (params.get('canonicalOnly') === '0') f.canonicalOnly = '0';
  const notUsed = params.get('notUsedTerm');
  if (notUsed && /^\d{4}$/.test(notUsed)) f.notUsedTerm = notUsed;
  // "Match: meaning" (Phase 7, Lane S); words is the default and stays out of the address
  if (params.get('mode') === 'meaning') f.mode = 'meaning';
  return f;
}

/** The order a list has when none is chosen: best match while searching, else newest first. */
export const autoSort = (q: string | undefined): string => (q?.trim() ? 'relevance' : 'year-desc');

/** The order to ask for: the chosen one, or the automatic one; best match needs a search. */
export function effectiveSort(sort: string | undefined, q: string | undefined): string {
  if (!sort || (sort === 'relevance' && !q?.trim())) return autoSort(q);
  return sort;
}

export function filtersToParams(f: LibraryFilters, q: string): URLSearchParams {
  const next = new URLSearchParams();
  if (q.trim()) next.set('q', q.trim());
  for (const key of LIST_KEYS) {
    const values = f[key];
    if (Array.isArray(values) && values.length) next.set(key, values.join(','));
  }
  for (const key of NUMBER_KEYS) if (f[key] !== undefined) next.set(key, String(f[key]));
  for (const key of BOOL_KEYS) if (f[key]) next.set(key, '1');
  if (f.canonicalOnly === '0') next.set('canonicalOnly', '0');
  if (f.notUsedTerm) next.set('notUsedTerm', f.notUsedTerm);
  // the automatic order stays out of the address; a chosen other one is kept
  if (f.sort && f.sort !== autoSort(q)) next.set('sort', f.sort);
  if (f.source === 'authored' || f.source === 'gitlab' || f.source === 'archive') next.set('source', f.source);
  if (f.mode === 'meaning') next.set('mode', 'meaning');
  return next;
}

export function activeFilterCount(f: LibraryFilters): number {
  let n = 0;
  for (const key of LIST_KEYS) n += (f[key]?.length ?? 0) > 0 ? 1 : 0;
  for (const key of NUMBER_KEYS) n += f[key] !== undefined ? 1 : 0;
  for (const key of BOOL_KEYS) n += f[key] ? 1 : 0;
  if (f.notUsedTerm) n += 1;
  return n;
}

